import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import ManageAccountsOutlined from '@mui/icons-material/ManageAccountsOutlined';
import { LoadingButton } from '../../design/LoadingButton';
import { color, monoLabel, radius } from '../../design/tokens';
import { getProfile } from '../../services/auth.service';
import { notAllowedRefusalAtom } from '../../services/api';
import { formatRemaining, useImpersonation, useStopImpersonation } from '../../services/impersonation';

const TICK_MS = 15_000;

/** The current time, re-rendering every TICK_MS while active (read fresh on every render). */
function useNow(active: boolean) {
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!active) return;
        const t = setInterval(() => setTick(n => n + 1), TICK_MS);
        return () => clearInterval(t);
    }, [active]);
    return Date.now();
}

/**
 * Shown under the top bar on every page while a Persera admin acts as someone else. Changes are made as
 * that user and logged; Stop returns to the admin's own account on /admin.
 */
export function ImpersonationBanner({ top = 0 }: { /** Sticks this far from the top (under the top bar). */ top?: number }) {
    const imp = useImpersonation();
    const navigate = useNavigate();
    const stop = useStopImpersonation();
    const qc = useQueryClient();
    const now = useNow(!!imp);
    const refusedAt = useAtomValue(notAllowedRefusalAtom);
    const [noticeOpen, setNoticeOpen] = useState(false);

    useEffect(() => {
        if (refusedAt) setNoticeOpen(true);
    }, [refusedAt]);

    // Past the end the server has already dropped it; reload so the UI follows
    const remaining = imp ? Date.parse(imp.expiresAt) - now : 0;
    useEffect(() => {
        if (imp && remaining <= 0) getProfile();
    }, [imp, remaining]);

    if (!imp) return null;

    const onStop = async () => {
        try {
            await stop.mutateAsync();
        } finally {
            // Everything cached was the other user's view
            qc.clear();
            await getProfile();
            navigate('/admin');
        }
    };

    return (
        <>
            <Box
                role="status"
                aria-label="Acting as another user"
                sx={{
                    position: 'sticky', top, zIndex: 19,
                    display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap',
                    px: { xs: 1.5, sm: 2.5 }, py: 0.75,
                    // Amber tint across the whole strip (no one-sided accent border)
                    backgroundColor: 'rgba(56, 38, 8, 0.92)',
                    backgroundImage: `linear-gradient(90deg, ${color.sparkSoft}, rgba(255, 177, 46, 0.06))`,
                    boxShadow: `0 8px 24px -14px ${color.spark}`,
                    backdropFilter: 'blur(12px)',
                }}
            >
                <ManageAccountsOutlined sx={{ color: color.spark, fontSize: 20, flexShrink: 0 }} />
                <Typography sx={{ flex: 1, minWidth: 0, fontSize: 14, color: color.text }}>
                    Acting as <Box component="strong" sx={{ fontWeight: 600 }}>{imp.targetName}</Box>
                    {imp.targetEmail && imp.targetEmail !== imp.targetName && (
                        <Box component="span" sx={{ color: color.textMuted }}> ({imp.targetEmail})</Box>
                    )}
                    <Box component="span" sx={{ color: color.textMuted }}>
                        {' · '}
                        <Box component="span" sx={{ ...monoLabel(12, '0.08em'), color: color.spark }}>changes are logged</Box>
                        {' · ends in '}{formatRemaining(Math.max(0, remaining))}
                    </Box>
                </Typography>
                <LoadingButton size="small" variant="contained" color="warning" onClick={onStop} loading={stop.isPending} loadingLabel="Stopping…"
                    sx={{ flexShrink: 0, borderRadius: `${radius.sm}px` }}>
                    Stop
                </LoadingButton>
            </Box>
            <Snackbar open={noticeOpen} autoHideDuration={6000} onClose={() => setNoticeOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="info" variant="filled" onClose={() => setNoticeOpen(false)} sx={{ backgroundColor: color.panelSolid, color: color.text }}>
                    Only {imp.targetName} can do that: account deletion and billing are off while acting.
                </Alert>
            </Snackbar>
        </>
    );
}
